import axios from "axios";

export const API_BASE_URL = process.env.VUE_APP_API_URL;

axios.defaults.timeout = 1200000;

const request = axios.create({
  baseURL: API_BASE_URL,
  timeout: 5000,
});

request.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.log(error);
    return Promise.reject(error);
  }
);

request.interceptors.response.use((res) => {
  return res.data;
});

export const sendEmailCode = async (email) => {
  return await request.get("/email/code", {
    params: {
      email,
    },
  });
};

/**
 * 邮箱登录
 * @param email 邮箱号码
 * @param code  验证码
 * @returns {Promise<AxiosResponse<T>>}
 */
export const loginByEmail = async (email, code) => {
  return await request.get("/user/login_email", {
    params: {
      email,
      code,
    },
  });
};

/**
 * 账户密码登录
 * @param account 用户账号
 * @param password 用户密码
 * @returns {Promise<AxiosResponse<T>>}
 */
export const loginByAccount = async (account, password) => {
  return await request.get("/user/login_account", {
    params: {
      account,
      password,
    },
  });
};

/**
 * 注册用户
 * @param account 用户名称
 * @param password 用户密码
 * @param email 用户邮箱
 * @param code 用户验证码
 * @param invitation 邀请码
 * @returns {Promise<AxiosResponse<T>>}
 */
export const register = async (account, password, email, code, invitation) => {
  return await request.get("/user/register", {
    params: {
      account,
      password,
      email,
      code,
      invitation,
    },
  });
};

/**
 * 更新账户密码
 * @param newPassword 新密码
 * @param email 邮箱
 * @param code 验证码
 * @returns {Promise<AxiosResponse<T>>}
 */
export const updatePassword = async (email, newPassword, code) => {
  return await request.get("/user/update_password", {
    params: {
      newPassword,
      email,
      code,
    },
  });
};

/**
 * 添加用户绩效
 * @param id 用户id
 * @param num 增加的积分数
 * @param reason 增加的积分理由
 * @returns {Promise<AxiosResponse<T>>}
 */
export const updatePointById = async (id, num, reason) => {
  return await request.get("/user/update_points", {
    params: {
      id,
      num,
      reason,
    },
  });
};

/**
 * 更改用户头像
 * @param id 用户id
 * @param file 头像文件
 * @returns {Promise<AxiosResponse<T>>}
 */
export const updateAvatarById = async (id, file) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("id", id);
  return await request.post("/user/update_avatar", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

/**
 * 分页查询
 * @param keyword 关键词
 * @param page 分页
 * @param limit 限制数量
 * @returns {Promise<AxiosResponse<T>>}
 */
export const selectPaperworkByPage = async (keyword, page, limit) => {
  return await request.get("/paperwork/select_page", {
    params: {
      keyword,
      page,
      limit,
    },
  });
};

/**
 * 文案上传
 * @param userName 用户名
 * @param paperwork 转发语
 * @returns {Promise<AxiosResponse<T>>}
 */
export const insertPaperwork = async (userName, paperwork) => {
  return await request.post("/paperwork/insert", {
      userName,
      paperwork,
  });
};

/**
 * 根据用户名选择所有的定时任务
 * @param userName
 * @returns {Promise<AxiosResponse<T>>}
 */
export const selectScheduleTaskByName = async (userName) => {
  return await request.get("/schedule_task/all", {
    params: {
      userName,
    },
  });
};

/**
 * 根据用户传递的关键词来查询在数据库中的数量
 * @param list
 * @returns {Promise<AxiosResponse<T>>}
 */
export const countCaseByKeyList = async (list) => {
  return await request.post("/case/count_key_list", list, {
    headers: { "Content-Type": "application/json" },
  });
};

export const changeMusicType = async (file, type, clientId) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("type", type);
  formData.append("clientId", clientId);

  return await request.post("/ffmpeg/change_music", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    timeout: 300000,
  });
};

/**
 *
 * @param name 任务名称
 * @param time 任务执行时间
 * @param type 任务类型
 * @param repeat 任务执行频率
 * @param endTimeStr 任务结束时间
 * @param description 任务描述
 * @param userName 创建者名称
 * @returns {Promise<AxiosResponse<T>>}
 */
export const registerSchedule = async (
  name,
  time,
  type,
  repeat = null,
  endTimeStr = null,
  description,
  createTime,
  userName
) => {
  return await request.get("/schedule_task/register", {
    params: {
      name,
      time,
      type,
      repeat,
      endTimeStr,
      description,
      userName,
    },
  });
};

/**
 * 根据用户名获取指定数量的任务清单
 * @param userName 用户名称
 * @param num 任务清单数量
 * @returns {Promise<AxiosResponse<T>>}
 */
export const selectScheduleByNameLimit = async (userName, num) => {
  return await request.get("/schedule_task/by_name_limit", {
    params: {
      userName,
      num,
    },
  });
};

/**
 * 通过地区名选取最近指定数量的数据
 * @param areaName 地区名
 * @param num 数量
 * @returns {Promise<AxiosResponse<T>>}
 */
export const selectAreaMarketInfo = async (areaName, num) => {
  return await request.get("/area_market/select_market", {
    params: {
      areaName,
      num,
    },
  });
};

/**
 * 统计数据库中有多少个地区数据
 * @returns {Promise<AxiosResponse<T>>}
 */
export const selectAreaList = async () => {
  return await request.get("/area_market/market_area", {
    params: {},
  });
};

/**
 * 分页获取任务信息
 * @param userName 用户名
 * @param page 页码
 * @param limit 限制
 * @returns {Promise<AxiosResponse<T>>}
 */
export const selectScheduleTaskByPage = async (userName, page, limit) => {
  return await request.get("/schedule_task/task_page", {
    params: {
      userName,
      page,
      limit,
    },
  });
};

/**
 * 根据定时任务目标更新信息
 * @param scheduleTask 任务对象
 * @returns {Promise<AxiosResponse<T>>}
 */
export const updateScheduleById = async (scheduleTask) => {
  return await request.post("/schedule_task/update", scheduleTask);
};

/**
 * 删除定时任务
 * @param scheduleTask
 * @returns {Promise<AxiosResponse<T>>}
 */
export const deleteSchedule = async (scheduleTask) => {
  return await request.post("/schedule_task/delete", scheduleTask);
};

/**
 * 音频转换
 * @param {File} file - 音频文件
 * @param type 改成文件类型
 * @param {string} clientId - SSE 客户端ID
 * @returns {Promise}
 */
export const changeMusic = async (file, type, clientId) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("type", type);
  formData.append("clientId", clientId);
  return await request.post("/ffmpeg/change_music", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

/**
 * 音频截取
 * @param {File} file - 音频文件
 * @param {string} beginTime - 开始时间 (格式 mm:ss 或 hh:mm:ss)
 * @param {string} endTime - 结束时间
 * @param {string} clientId - SSE 客户端ID
 * @returns {Promise}
 */
export const splitMusic = async (file, beginTime, endTime, clientId) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("beginTime", beginTime);
  formData.append("endTime", endTime);
  formData.append("clientId", clientId);
  return await request.post("/ffmpeg/split_music", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  });
};

/**
 * 视频音乐提取
 * @param {File} file - 视频文件
 * @param {string} clientId - SSE 客户端ID
 * @returns {Promise}
 */
export const extractVideoMusic = async (file, clientId) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("clientId", clientId);
  // 如果后端需要指定音频格式，可在此添加参数，例如 audioFormat: 'mp3'
  return await request.post("/ffmpeg/video_music", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

/**
 * 视频截取
 * @param {File} file - 视频文件
 * @param {string} beginTime - 开始时间
 * @param {string} endTime - 结束时间
 * @param {string} clientId - SSE 客户端ID
 * @returns {Promise}
 */
export const splitVideo = async (file, beginTime, endTime, clientId) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("beginTime", beginTime);
  formData.append("endTime", endTime);
  formData.append("clientId", clientId);
  return await request.post("/ffmpeg/split_video", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

/**
 * 下载文件并自动触发浏览器下载
 * @param fileName
 * @param {string} [customName] - 自定义下载文件名（可选，默认使用 filename）
 * @returns {Promise} - 下载成功或失败的 Promise
 */
export const downloadFile = async (fileName, customName) => {
  try {
    const blob = await request.get("/file/download", {
      params: { fileName },
      responseType: "blob",
      timeout: 1200000
    });

    const downloadName = customName || fileName;
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = downloadName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("下载失败", error);
  }
};

/**
 * 创建短时的即时连接
 * @param clientId
 * @returns {EventSource}
 */
export const createSSE = (clientId) => {
  return new EventSource(`${API_BASE_URL}/sse/connect/?clientId=${clientId}`);
};

/**
 * 分页查询
 * @param fileName 关键词
 * @param pageNum 分页
 * @param limit 限制数量
 * @returns {Promise<AxiosResponse<T>>}
 */
export const selectCaseByPage = async (fileName, pageNum, limit) => {
  return await request.get("/case/search_by_name", {
    params: {
      fileName,
      pageNum,
      limit,
    },
  });
};

/**
 * 方案下载
 * @param path 方案下载的存储路径
 * @returns {Promise<void>}
 */
export const downloadCase = async (path) => {
  try {
    return await request.get("/cos/case", {
      params: { key: path },
    });
  } catch (e) {
    console.error(e);
  }
};

/**
 * 创建订单
 * @param money 金额单位分
 * @param description 订单描述
 * @returns {Promise<AxiosResponse<T>>}
 */
export const createNativePay = async (money, description) => {
  const formData = new FormData();
  formData.append("money", money);
  formData.append("description", description);
  return await request.post("/pay/native", formData);
};

export const downloadMarketInfo = async (headList, dataList) => {
  return await request.post("/area_market/download_market", {
    headList,
    dataList,
  });
};
